import { useState, type FormEvent } from 'react'
import { KeyRound, Phone, User } from 'lucide-react'
import { customerLoginSchema, customerRegisterSchema } from '@najah/core'
import { Alert, Button, Input, Logo, cn } from '@najah/ui'
import { useAuth } from '../lib/auth'
import { ApiError } from '../lib/api'

export function AuthScreen({ onDone }: { onDone?: () => void }) {
  const { login, register } = useAuth()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [phone, setPhone] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)

    const schema = mode === 'login' ? customerLoginSchema : customerRegisterSchema
    const payload = mode === 'login' ? { phone, password } : { phone, fullName, password }
    const parsed = schema.safeParse(payload)

    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) next[String(issue.path[0] ?? '_')] ??= issue.message
      setErrors(next)
      return
    }

    setErrors({})
    setBusy(true)
    try {
      if (mode === 'login') await login(parsed.data as never)
      else await register(parsed.data as never)
      onDone?.()
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) setErrors(err.fields)
        else setFormError(err.message)
      } else {
        setFormError('حدث خطأ غير متوقع')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center px-6 py-10">
      <div className="mb-8 text-center">
        {/* الشعار أخضر+ذهبي مصمَّم لخلفية فاتحة — في الوضع الداكن يقف على بطاقة كريمية */}
        <div className="mx-auto inline-block rounded-2xl px-6 py-3 dark:bg-[#FBF8F1]">
          <Logo variant="store" className="mx-auto h-28" />
        </div>
        <p className="mt-3 text-sm text-fg-muted">
          {mode === 'login' ? 'سجّل الدخول لمتابعة طلباتك' : 'أنشئ حساباً لتطلب من المكتبة'}
        </p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-bg-subtle p-1">
        {([['login', 'تسجيل الدخول'], ['register', 'حساب جديد']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setMode(key); setErrors({}); setFormError(null) }}
            className={cn(
              'rounded-lg py-2.5 text-sm font-medium transition-colors',
              mode === key ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {formError && <Alert tone="danger" className="mb-4">{formError}</Alert>}

      <form onSubmit={submit} noValidate className="space-y-4">
        <Input
          label="رقم الهاتف" required ltr inputMode="tel" autoComplete="tel"
          placeholder="0550 12 34 56"
          value={phone} onChange={(e) => setPhone(e.target.value)}
          error={errors.phone}
          startIcon={<Phone className="size-4" />}
        />

        {mode === 'register' && (
          <Input
            label="الاسم الكامل" required
            value={fullName} onChange={(e) => setFullName(e.target.value)}
            error={errors.fullName}
            startIcon={<User className="size-4" />}
          />
        )}

        <Input
          label="كلمة المرور" required type="password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          value={password} onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          startIcon={<KeyRound className="size-4" />}
        />

        <Button type="submit" size="lg" fullWidth loading={busy} className="mt-2">
          {mode === 'login' ? 'دخول' : 'إنشاء الحساب'}
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-fg-subtle">
        يمكنك تصفّح المنتجات دون حساب — الحساب مطلوب لإتمام الطلب فقط.
      </p>
    </div>
  )
}
